<template>
  <master-layout>
    <div class="px-5 pt-6 pb-[120px]">
      <BackButton :customBack="back" class="mb-4" />
      <h1 class="text-[1.75rem] font-black text-[#42362B] tracking-[-0.03em] mb-6">
        PhotoDrop Upload Test
      </h1>

      <!-- Upload Section -->
      <div class="mb-8">
        <p class="text-[0.875rem] text-[#6B6B6B] mb-4">
          Select photos to upload. They'll be sent to the API and you'll get a 6-digit code.
        </p>

        <label
          class="flex flex-col items-center justify-center w-full h-[160px] border-2 border-dashed border-[#D6D3CF] rounded-[16px] cursor-pointer active:bg-[#F3F2EF]"
        >
          <ion-icon
            :icon="cloudUploadOutline"
            class="text-[2.25rem] text-[#9CA3AF] mb-2"
          ></ion-icon>
          <span class="text-[0.875rem] text-[#6B6B6B] font-medium">Tap to select photos</span>
          <span class="text-[0.75rem] text-[#9CA3AF] mt-1">JPG, PNG, MP4 — up to 10 files</span>
          <input
            ref="fileInput"
            type="file"
            accept="image/*,video/*"
            multiple
            class="hidden"
            @change="onFilesSelected"
          />
        </label>
      </div>

      <!-- Selected Files Preview -->
      <div v-if="selectedFiles.length" class="mb-6">
        <h2 class="text-[1rem] font-bold text-[#42362B] mb-3">
          {{ selectedFiles.length }} file{{ selectedFiles.length > 1 ? 's' : '' }} selected
        </h2>
        <div class="grid grid-cols-3 gap-2 mb-4">
          <div
            v-for="(file, i) in filePreviews"
            :key="i"
            class="aspect-square rounded-[8px] overflow-hidden bg-[#EEECE8]"
          >
            <img v-if="file.type === 'image'" :src="file.url" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full flex items-center justify-center">
              <ion-icon :icon="videocamOutline" class="text-[1.5rem] text-[#6B6B6B]"></ion-icon>
            </div>
          </div>
        </div>

        <button
          class="w-full py-[14px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold flex items-center justify-center gap-2"
          :disabled="uploading"
          @click="upload"
        >
          <ion-spinner v-if="uploading" name="crescent" class="w-[20px] h-[20px]"></ion-spinner>
          <template v-else>
            <ion-icon :icon="cloudUploadOutline" class="text-[1.125rem]"></ion-icon>
            Upload & Generate Code
          </template>
        </button>
      </div>

      <!-- Result -->
      <div v-if="resultCode" class="bg-white rounded-[16px] p-6 text-center mb-6">
        <p class="text-[0.875rem] text-[#6B6B6B] mb-2">Your code</p>
        <p class="text-[3rem] font-black text-[#42362B] tracking-[8px] mb-2">{{ resultCode }}</p>
        <p class="text-[0.875rem] text-[#6B6B6B] mb-6">
          {{ resultCount }} photo{{ resultCount > 1 ? 's' : '' }} uploaded
        </p>
        <button
          class="w-full py-[14px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold mb-3"
          @click="copyCode"
        >
          Copy Code
        </button>
        <button
          class="w-full py-[14px] text-[#3D3A35] rounded-full text-[1rem] font-semibold"
          @click="goToPhotoDrop"
        >
          Enter Code in PhotoDrop
        </button>
      </div>

      <!-- Error -->
      <p v-if="error" class="text-[0.875rem] italic text-[#EC4E20] text-center mt-4">{{ error }}</p>

      <!-- Upload Another -->
      <button
        v-if="resultCode"
        class="w-full py-[14px] text-[#6B6B6B] text-[0.875rem] font-medium mt-2"
        @click="reset"
      >
        Upload More Photos
      </button>
    </div>
  </master-layout>
</template>

<script>
import axios from 'axios';
import { cloudUploadOutline, videocamOutline } from 'ionicons/icons';
import { toastController } from '@ionic/vue';
import BackButton from '@/components/common/BackButton.vue';

const API_KEY = 'photodropapikey';

export default {
  name: 'PhotoDropUploadTest',
  components: { BackButton },
  data() {
    return {
      selectedFiles: [],
      filePreviews: [],
      uploading: false,
      resultCode: null,
      resultCount: 0,
      error: null,
    };
  },
  created() {
    this.cloudUploadOutline = cloudUploadOutline;
    this.videocamOutline = videocamOutline;
  },
  methods: {
    back() {
      this.$router.go(-1);
    },
    onFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      this.selectedFiles = files.slice(0, 10);
      this.filePreviews = this.selectedFiles.map(f => ({
        type: f.type.startsWith('video') ? 'video' : 'image',
        url: URL.createObjectURL(f),
      }));
      this.resultCode = null;
      this.error = null;
    },
    async fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async upload() {
      if (!this.selectedFiles.length) return;
      this.uploading = true;
      this.error = null;
      this.resultCode = null;

      try {
        const files = await Promise.all(
          this.selectedFiles.map(async f => ({
            data: await this.fileToBase64(f),
            mimeType: f.type || 'image/jpeg',
          })),
        );

        const { data } = await axios.post(
          process.env.VUE_APP_API_URL + '/photo/photodrop/upload',
          { files },
          { headers: { 'X-API-KEY': API_KEY } },
        );

        this.resultCode = data.code;
        this.resultCount = data.photoCount;
      } catch (e) {
        const msg = e.response?.data?.message || e.message;
        this.error = `Upload failed: ${msg}`;
      } finally {
        this.uploading = false;
      }
    },
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.resultCode);
      } catch {
        // ignore
      }
      const toast = await toastController.create({
        message: 'Code copied!',
        duration: 1500,
        position: 'bottom',
        mode: 'ios',
      });
      toast.style.zIndex = '99999';
      await toast.present();
    },
    goToPhotoDrop() {
      this.$router.push('/photodrop');
    },
    reset() {
      this.selectedFiles = [];
      this.filePreviews = [];
      this.resultCode = null;
      this.resultCount = 0;
      this.error = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
  },
};
</script>
